import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { UIRouter }                                      from '@uirouter/core';
import { TranslateService }                              from '@ngx-translate/core';

import { ApiService }      from '../../app-lite/api/api.service';
import { MessagesService } from '../../app-lite/messages/messages.service';
import { SessionService }  from '../../app-lite/sessions/session.service';
import { TableService }    from '../../app-lite/table/table.service';

import { CustomerVehicleActionsComponent } from './customer.vehicle.actions.component';
import { VVGarageFactory }                 from '../vv-garage/vv-garage.service';

@Component({
  selector   : 'customer-vehicles',
  template: require('./customer.vehicles.html')
})
export class CustomerVehiclesComponent implements OnInit {
  @Input() customer: any;
  @Input() vehicles: any[];

  columns: any[];
  company: any;
  fleetCodeLabel: string;
  regoLabel: string;
  session: any;
  showCustomerVehicles: boolean;
  tableCacheKey: string;
  tableSettings: any;
  VVGarage: any;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    @Inject(forwardRef(() => SessionService)) private sessionService: SessionService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    @Inject(forwardRef(() => VVGarageFactory)) private vvGarageFactory: VVGarageFactory,
    private router: UIRouter,
    private translateService: TranslateService
  ) {}

  ngOnInit () {
    this.company              = this.sessionService.company();
    this.session              = this.sessionService.base();
    this.showCustomerVehicles = !!this.vehicles.length;
    this.tableCacheKey        = 'customerVehicles';
    this.VVGarage             = this.vvGarageFactory.create(this.customer);

    if (this.company.company_variable_fields.plate_number_name) {
      this.regoLabel = this.company.company_variable_fields.plate_number_name;
    }
    else {
      this.regoLabel = this.sessionService.countrySettings().rego_label;
    }

    if (this.company.company_variable_fields.fleet_code_name) {
      this.fleetCodeLabel = this.company.company_variable_fields.fleet_code_name;
    }
    else {
      this.fleetCodeLabel = this.translateService.instant('VEHICLE_SPACE.FLEET_CODE');
    }

    this.columns = [
      {
        field : 'plate_number',
        header: {
          text: this.regoLabel,
        },
        classes: 'all-caps'
      },
      {
        field : 'make',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MAKE')
        }
      },
      {
        field : 'model',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL')
        }
      },
      {
        field : 'model_code',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL_CODE')
        }
      },
      {
        field : 'model_series',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL_SERIES')
        }
      },
      {
        classes: 'text-center',
        field  : 'archived',
        header : {
          classes: 'text-center',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.STATUS')
        },
        isHtml: true,
        pipe  : 'archived',
      },
      {
        component: {
          bindings: {
            outputs: {
              onAddVVGarageClick   : (vehicle) => this.addVehicleToVVGarageCustomer(vehicle),
              onRemoveVVGarageClick: (vehicle) => this.removeVehicleFromVVGarageCustomer(vehicle),
              onEditClick          : (vehicle) => this.router.stateService.go('app.vehicle', {
                customer_id: vehicle.customer_id,
                id         : vehicle.id
              }),
              onEventClick: (vehicle) => this.router.stateService.go('app.event', {
                customer_id: vehicle.customer_id,
                vehicle_id : vehicle.id
              }),
              onInvoiceClick: (vehicle) => this.router.stateService.go('app.invoice', {
                customer_id: vehicle.customer_id,
                vehicle_id : vehicle.id
              })
            }
          },
          enabled: true,
          factory: CustomerVehicleActionsComponent,
        },
        sortable: false
      }
    ];

    if (this.company.company_settings.show_fleet_code) {
      this.columns.splice(this.columns.length - 1, 0, {
        field : 'fleet_code',
        header: {
          text: this.fleetCodeLabel
        }
      });
    }

    this.tableSettings = {
      cache: {
        enabled: true,
        key    : this.tableCacheKey
      },
      columns: this.columns,
      empty  : {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_VEHICLES_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 5,
        pageSizes : [ 5, 10, 25, 50 ],
        queryCount: () => this.apiService.get('/customers/vehicle_count/' + this.customer.id)
      },
      rows: {
        highlight: {
          enabled: true
        }
      },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
        direction: 'asc',
        field    : 'plate_number'
      })
    };
  }

  getVehicles ( params ): Promise<any> {
    return this.apiService.get('/customers/vehicle_list', [
      this.customer.id,
      params.offset,
      params.pageSize,
      params.field,
      params.direction
    ]).then((vehicles) => {
      this.vehicles = vehicles;

      return vehicles;
    });
  }

  onRowDoubleClick ( event: any ): void {
    this.router.stateService.go('app.vehicle', {
      customer_id: this.customer.id,
      id         : event.row.id
    });
  }

  addVehicleToVVGarageCustomer ( vehicle: any ): void {
    this.VVGarage.add_vehicle_to_customer(vehicle).then(() => {
      this.messagesService.show(this.translateService.instant('VV_GARAGE_SPACE.VEHICLE_ADDED_TO_CUSTOMER'), 'success');

      this.router.stateService.reload();
    }).catch((err) => {
      this.messagesService.error(err);
    });
  }

  removeVehicleFromVVGarageCustomer ( vehicle: any ): void {
    this.VVGarage.delete_customer_vehicle(vehicle).then(() => {
      this.messagesService.show(this.translateService.instant('VV_GARAGE_SPACE.VEHICLE_REMOVED_FROM_CUSTOMER'), 'success');

      this.router.stateService.reload();
    }).catch((err) => {
      this.messagesService.error(err);
    });
  }
}
